import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanResourcesAction,
  createResourceAction,
  resourcesEntitiesSelector,
  resourcesIdsSelector,
  getResourceListAction,
  isLoadingResourcesSelector,
  remoteTotalSelector,
  updateResourceAction,
} from 'state/slices/resource';
import { usePagination } from 'hooks/pagination';
import { Resource, PaginationData } from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateResourceData } from 'api/resource';

import { unwrapResult } from '@reduxjs/toolkit';
import {
  ResourceCategoryEnum,
  ResourceCategoryMapping,
  ResourceCostAccountEnum,
  ResourceCostAccountMapping,
} from 'constants/resource';
import { UnitEnum, UnitMapping } from 'constants/unit';

import EditModal, { ResourceFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';

const ResourcePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const resourceEntities = useSelector(resourcesEntitiesSelector);
  const resourceIds = useSelector(resourcesIdsSelector);
  const isRemoteLoading = useSelector(isLoadingResourcesSelector);
  const [isLoading, setLoading] = useState(false);

  const resources = resourceIds.map((resourceId) => {
    const entity = resourceEntities[resourceId];
    if (!entity) return undefined;
    const tableResource: Resource & {
      key?: string,
    } = {
      ...entity,
      key: resourceId.toString(),
    };
    return tableResource;
  }).filter((tableResource) => !!tableResource) as (Resource & { key: string})[];

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getResourceListAction(paginationData));
  };
  const pagination = usePagination<object>( // eslint-disable-line @typescript-eslint/ban-types
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanResourcesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [resourceForm] = Form.useForm<ResourceFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditResource, setActiveEditResource] = useState<Resource>();

  const openModal = (resourceId: string) => {
    const resourceEntity = resourceEntities[resourceId];
    setActiveEditResource(resourceEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditResource(undefined);
    setEditModalOpen(false);
    resourceForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await resourceForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: ResourceFormData = resourceForm.getFieldsValue();

    const resourceData: CreateResourceData = {
      ...formData,
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditResource) {
        const updateResult = await dispatch(updateResourceAction({
          id: activeEditResource.id,
          data: {
            ...resourceData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createResourceAction(resourceData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Item ${activeEditResource ? 'atualizado' : 'criado'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar item. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage extra={(
      <Extra
        onSearch={pagination.onSearch}
        onAddClick={() => openModal('')}
      />
    )}
    >
      <>
        <Table
          dataSource={resources}
          pagination={pagination.getInternal()}
          loading={isRemoteLoading}
        >
          <Table.Column
            title="Item"
            key="name"
            dataIndex="name"
          />
          <Table.Column
            title="Unidade"
            key="unit"
            dataIndex="unit"
            render={(
              unit: UnitEnum,
            ) => UnitMapping[unit].name}
          />
          <Table.Column
            title="Categoria"
            key="category"
            dataIndex="category"
            render={(
              category: ResourceCategoryEnum,
            ) => ResourceCategoryMapping[category].name}
          />
          <Table.Column
            title="Conta de Custo"
            key="costAccount"
            dataIndex="costAccount"
            render={(
              costAccount: ResourceCostAccountEnum,
            ) => ResourceCostAccountMapping[costAccount].name}
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(resourceId: string) => (
              <ActionButton onClick={() => openModal(resourceId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={resourceForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curResource={activeEditResource}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default ResourcePage;
